import { ref, watch, computed, onUnmounted } from 'vue'
import {
  initAudioContext,
  setupAudioGraph,
  updateChannelsCount,
  updateChannelVolume
} from '@/utils/audioChannelProcessor'

// shared ref for the audio element
// const audioSourceUrl = ref()
const audioElement = ref()
const duration = ref()
const currentTime = ref()
const durationFormatted = ref('00:00:00')
const currentTimeFormatted = ref('00:00:00')

// Audio Playback Properties
const volume = ref(1)
const playbackRate = ref(1)
const playing = ref(false)
const isLoading = ref(false)
const isBuffering = ref(true)
const audioError = ref(null)

// Buffer health tracking
const lastBufferCheck = ref(0)
const bufferHealth = ref(1) // 0-1 value indicating buffer health

// Segment playback properties
const isSegmentMode = ref(false)
const segmentStart = ref(null)
const segmentEnd = ref(null)
const segmentDuration = ref(null)

// Audio Channel Controls
const channelCount = ref(8)
const channelControls = ref([])
const isolatedChannelIndex = ref(null)
const channelDataMapping = ref([0, 2, 1, 7, 5, 6, 3, 4])

const audioContext = ref(null)
const audioWorkletNode = ref(null)

// Monitor memory usage (for debugging, only works in Chrome)
const ENABLE_MEMORY_MONITOR = false
let memoryMonitorInterval = null
const MEMORY_WARNING_THRESHOLD = 0.7 // 70% usage
if (
  ENABLE_MEMORY_MONITOR &&
  typeof window !== 'undefined' &&
  window.performance &&
  performance.memory
) {
  memoryMonitorInterval = setInterval(() => {
    if (performance.memory) {
      const used = performance.memory.usedJSHeapSize
      const total = performance.memory.totalJSHeapSize
      const ratio = used / total
      if (ratio > MEMORY_WARNING_THRESHOLD) {
        console.warn('🧠 Memory usage warning:', {
          usedJSHeapSize: (used / 1048576).toFixed(2) + ' MB',
          totalJSHeapSize: (total / 1048576).toFixed(2) + ' MB',
          usageRatio: (ratio * 100).toFixed(1) + '%'
        })
      }
    }
  }, 15000)
}

export const useAudioPlayer = () => {
  const init = async ({ domAudioElement, channels }) => {
    audioElement.value = domAudioElement

    // * Setup event handlers
    setupAudioElementEvents(domAudioElement)

    // * Setup channels
    channelCount.value = channels.length
    channelControls.value = channels.map(({ micId, name }, index) => ({
      id: `channelVolumeControl${micId}`,
      title: name,
      volume: 1,
      level: 0,
      muted: false,
      disabled: false,
      processorMapIndex: channelDataMapping.value[index]
    }))

    //  * Setup audio processing
    const { isSuccess: ctxSuccess, audioContext: ctx } = await initAudioContext()
    if (ctxSuccess) {
      audioContext.value = ctx
    }

    // setupAudioGraph that creates the workletNode
    const { isSuccess: agSuccess, workletNode } = await setupAudioGraph({
      audioContext: audioContext.value,
      channelCount: channelCount.value,
      audioElement: audioElement.value
    })
    if (agSuccess) {
      audioWorkletNode.value = workletNode

      let lastLevelUpdate = 0

      // Add message handler to workletNode that updates the audio level of the channel controls
      audioWorkletNode.value.port.onmessage = (event) => {
        const now = performance.now()

        // Throttle level updates to 30fps instead of 60fps+
        if (now - lastLevelUpdate < 33) return
        lastLevelUpdate = now

        const levels = event.data.channelLevels
        // console.log('🚀 ~ audioWorkletNode.value.port.onmessage ~ levels:', levels)

        channelControls.value.forEach((control) => {
          control.level = Math.min(levels[control.processorMapIndex] ?? 0 * 2, 1) // Scale RMS to 0-1 range
        })
      }

      updateChannelsCount({
        workletNode: audioWorkletNode.value,
        value: channelCount.value
      })
    }
  }

  // * dom audio element event listeners
  const setupAudioElementEvents = (domAudioElement) => {
    domAudioElement.addEventListener('loadedmetadata', () => {
      isLoading.value = false
      isBuffering.value = false
    })

    let lastTimeUpdate = 0
    let timeUpdateCount = 0

    domAudioElement.addEventListener('timeupdate', () => {
      currentTime.value = domAudioElement.currentTime

      const now = performance.now()

      // Throttle timeupdate processing to reduce load
      if (now - lastTimeUpdate < 50) return // Max 20fps instead of 60fps
      lastTimeUpdate = now

      // Check buffer health every 2 seconds
      timeUpdateCount++
      if (timeUpdateCount % 40 === 0) {
        checkBufferHealth(domAudioElement)
      }

      const previousTime = currentTime.value

      // Detect and handle jumps
      if (previousTime && currentTime.value - previousTime > 1.5) {
        console.warn('🚨 Playback jump detected:', {
          from: previousTime,
          to: currentTime.value,
          jump: currentTime.value - previousTime,
          buffering: isBuffering.value,
          playing: playing.value,
          bufferHealth: bufferHealth.value
        })
      }

      if (isSegmentMode.value) {
        currentTimeFormatted.value = getTimeFormatted(currentTime.value - segmentStart.value)
      } else {
        currentTimeFormatted.value = getTimeFormatted(currentTime.value)
      }
    })

    domAudioElement.addEventListener('ratechange', () => {
      volume.value = domAudioElement.volume
    })

    domAudioElement.addEventListener('ratechange', () => {
      playbackRate.value = domAudioElement.playbackRate
    })

    domAudioElement.addEventListener('durationchange', () => {
      if (isSegmentMode.value) {
        duration.value = segmentEnd.value - segmentStart.value
      } else {
        duration.value = domAudioElement.duration
      }
      durationFormatted.value = getTimeFormatted(duration.value)
    })
  }

  const checkBufferHealth = (domAudioElement) => {
    try {
      const buffered = domAudioElement.buffered
      let bufferedTime = 0

      // More efficient buffer calculation
      const bufferedLength = buffered.length
      for (let i = 0; i < bufferedLength; i++) {
        bufferedTime += buffered.end(i) - buffered.start(i)
      }

      bufferHealth.value = Math.min(bufferedTime / (domAudioElement.duration || 1), 1)

      // Only warn if buffer health is critically low
      if (ENABLE_MEMORY_MONITOR && bufferHealth.value < 0.3) {
        console.warn('🪫Low buffer health:', {
          bufferHealth: bufferHealth.value,
          bufferedTime,
          duration: domAudioElement.duration,
          currentTime: currentTime.value
        })
      }
    } catch (error) {
      // Silently handle buffer check errors
      console.debug('Buffer check error:', error)
    }
  }

  const segmentRelativeCurrentTime = computed(() => {
    if (isSegmentMode.value && segmentStart.value !== null) {
      return currentTime.value - segmentStart.value
    }
    return null
  })

  // Watch for currentTime changes to handle segment end
  watch(currentTime, (newTime) => {
    if (isSegmentMode.value && segmentEnd.value && newTime >= segmentEnd.value) {
      pause()
      // Optionally reset to segment start
      // setCurrentTime(segmentStart.value)
    }
  })

  const play = () => {
    if (!audioElement.value) return

    // In segment mode, always start from segment start
    if (
      (isSegmentMode.value && currentTime.value < segmentStart.value) ||
      (segmentEnd.value && currentTime.value >= segmentEnd.value)
    ) {
      setCurrentTime(segmentStart.value)
    }

    audioElement.value.play()
    playing.value = true
  }

  const pause = () => {
    if (!audioElement.value) return
    audioElement.value.pause()
    playing.value = false
  }

  const togglePlayPause = () => {
    if (!audioElement.value) return
    audioElement.value.paused ? play() : pause()
  }

  const setVolume = (volume) => {
    if (!audioElement.value) return
    audioElement.value.volume = volume
  }

  const setPlaybackRate = (rate) => {
    if (!audioElement.value) return
    audioElement.value.playbackRate = rate
  }

  const setCurrentTime = (time, relativeToSegment = false) => {
    if (!audioElement.value) return

    if (isSegmentMode.value) {
      const absoluteTime = relativeToSegment ? segmentStart.value + time : time

      // Ensure time is within segment boundaries
      const constrainedTime = Math.min(Math.max(absoluteTime, segmentStart.value), segmentEnd.value)
      audioElement.value.currentTime = constrainedTime
    } else {
      audioElement.value.currentTime = time
    }
  }

  const rewind = (seconds) => {
    if (!audioElement.value) return
    setCurrentTime(Math.max(0, audioElement.value.currentTime - seconds))
  }

  const forward = (seconds) => {
    if (!audioElement.value) return
    setCurrentTime(Math.min(audioElement.value.duration, audioElement.value.currentTime + seconds))
  }

  const goToStart = () => {
    if (!audioElement.value) return

    if (isSegmentMode.value) {
      setCurrentTime(segmentStart.value)
    } else {
      setCurrentTime(0)
    }
  }

  const goToEnd = () => {
    if (!audioElement.value) return

    if (isSegmentMode.value && segmentEnd.value) {
      setCurrentTime(segmentEnd.value)
    } else {
      setCurrentTime(audioElement.value.duration - 0.1)
    }
  }

  const enableSegmentMode = (start, end) => {
    segmentStart.value = start || 0
    segmentEnd.value = end || null
    isSegmentMode.value = true

    // Set playhead to segment start
    setCurrentTime(segmentStart.value)

    return {
      start: segmentStart.value,
      end: segmentEnd.value
    }
  }

  const playSegment = (start, end) => {
    enableSegmentMode(start, end)
    play()
  }

  const loopSegment = (start, end, shouldLoop = true) => {
    enableSegmentMode(start, end)

    // Add an event listener for when the segment ends
    if (shouldLoop && audioElement.value) {
      const handleSegmentEnd = () => {
        if (currentTime.value >= segmentEnd.value) {
          setCurrentTime(segmentStart.value)
          play()
        }
      }

      // Remove existing listeners to avoid duplicates
      audioElement.value.removeEventListener('timeupdate', handleSegmentEnd)

      if (shouldLoop) {
        audioElement.value.addEventListener('timeupdate', handleSegmentEnd)
      }
    }

    play()
  }

  /**
   * Toggle channel isolation - when a channel is isolated, only it will be audible
   * @param {number} channelIndex - Index of the channel to isolate
   */
  const onChannelIsolation = (channelIndex) => {
    const isResettingIsolation = isolatedChannelIndex.value === channelIndex
    isolatedChannelIndex.value = isResettingIsolation ? null : channelIndex

    channelControls.value.forEach((control, index) => {
      const shouldBeActive = isResettingIsolation || channelIndex === index
      const isChannelEnabled = !control.muted && shouldBeActive

      control.disabled = !isChannelEnabled
      updateChannelVolume({
        workletNode: audioWorkletNode.value,
        index: control.processorMapIndex,
        value: isChannelEnabled ? control.volume : 0
      })
    })
  }

  /**
   * Toggle mute state for a specific channel
   * @param {number} channelIndex - Index of the channel to mute/unmute
   */
  const onChannelMute = (channelIndex) => {
    const control = channelControls.value[channelIndex]
    control.muted = !control.muted

    const channelVolume = control.muted ? 0 : control.volume
    control.disabled = control.muted

    updateChannelVolume({
      workletNode: audioWorkletNode.value,
      index: control.processorMapIndex,
      value: channelVolume
    })
  }

  const onChannelVolumeChange = ({ index, value }) => {
    const control = channelControls.value[index]
    updateChannelVolume({
      workletNode: audioWorkletNode.value,
      index: control.processorMapIndex,
      value
    })
  }

  const handleKeyDownShortcuts = (event) => {
    // Skip all shortcuts if audio is loading or buffering
    if (isLoading.value || isBuffering.value) {
      return
    }

    const isCtrlPressed = event.ctrlKey
    const shortcuts = {
      F1: isCtrlPressed
        ? {
            action: () => onChannelIsolation(0),
            description: 'Isolate channel 1',
            requiresCtrl: true
          }
        : { action: () => rewind(5), description: 'Rewind 5 seconds' },
      F2: isCtrlPressed
        ? {
            action: () => onChannelIsolation(1),
            description: 'Isolate channel 2',
            requiresCtrl: true
          }
        : { action: () => forward(5), description: 'Forward 5 seconds' },
      F3: isCtrlPressed
        ? {
            action: () => onChannelIsolation(2),
            description: 'Isolate channel 3',
            requiresCtrl: true
          }
        : { action: () => rewind(10), description: 'Rewind 10 seconds' },
      F4: isCtrlPressed
        ? {
            action: () => onChannelIsolation(3),
            description: 'Isolate channel 4',
            requiresCtrl: true
          }
        : { action: () => forward(10), description: 'Forward 10 seconds' },
      F5: isCtrlPressed
        ? {
            action: () => onChannelIsolation(4),
            description: 'Isolate channel 5',
            requiresCtrl: true
          }
        : null,
      F6: isCtrlPressed
        ? {
            action: () => onChannelIsolation(5),
            description: 'Isolate channel 6',
            requiresCtrl: true
          }
        : null,
      F7: isCtrlPressed
        ? {
            action: () => onChannelIsolation(6),
            description: 'Isolate channel 7',
            requiresCtrl: true
          }
        : { action: () => rewind(5), description: 'Rewind 5 seconds' },
      F8: isCtrlPressed
        ? {
            action: () => onChannelIsolation(7),
            description: 'Isolate channel 8',
            requiresCtrl: true
          }
        : { action: () => setPlaybackRate(1.3), description: 'Fast speed (1.3x)' },
      F9: { action: togglePlayPause, description: 'Play/Pause' },
      F10: { action: () => setPlaybackRate(1), description: 'Normal speed (1.0x)' },
      F11: { action: () => setPlaybackRate(0.7), description: 'Slow speed (0.7x)' },
      Home: { action: () => goToStart(), description: 'Go to start', requiresCtrl: true },
      End: { action: () => goToEnd(), description: 'Go to end', requiresCtrl: true }
    }

    const shortcut = shortcuts[event.key] || shortcuts[event.code]
    if (shortcut) {
      // Skip if this shortcut requires Ctrl and Ctrl is not pressed
      if (shortcut.requiresCtrl && !isCtrlPressed) return

      event.preventDefault()
      shortcut.action()

      console.info('Shortcut triggered:', shortcut.description)
    }
  }

  // Utility Function for Time Formatting
  const getTimeFormatted = (time) => {
    if (!time) return '00:00:00'
    const seconds = Math.floor(time)
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  const reset = () => {
    // Reset audio state
    if (audioElement.value) {
      audioElement.value.pause()
      audioElement.value.src = ''
      audioElement.value.load()
    }
    playing.value = false
    isLoading.value = false
    isBuffering.value = true
    audioError.value = null

    // Reset time properties
    currentTime.value = 0
    duration.value = 0
    currentTimeFormatted.value = '00:00:00'
    durationFormatted.value = '00:00:00'

    // Reset playback properties
    volume.value = 1
    playbackRate.value = 1

    // Reset segment properties
    isSegmentMode.value = false
    segmentStart.value = null
    segmentEnd.value = null
    segmentDuration.value = null

    // Reset channel isolation
    isolatedChannelIndex.value = null

    // Reset channel controls to default values
    if (channelControls.value.length > 0) {
      channelControls.value.forEach((control, index) => {
        control.volume = 1
        control.level = 0
        control.muted = false
        control.disabled = false

        // Reset channel volume in the audio worklet if it exists
        if (audioWorkletNode.value) {
          updateChannelVolume({
            workletNode: audioWorkletNode.value,
            index,
            value: 1
          })
        }
      })
    }

    if (memoryMonitorInterval) {
      clearInterval(memoryMonitorInterval)
      memoryMonitorInterval = null
    }
  }

  return {
    init,
    audioElement,
    duration,
    durationFormatted,
    currentTime,
    segmentRelativeCurrentTime,
    currentTimeFormatted,
    volume,
    playbackRate,
    isLoading,
    isBuffering,
    audioError,

    // Basic controls
    togglePlayPause,
    setVolume,
    setPlaybackRate,
    rewind,
    forward,
    setCurrentTime,
    goToStart,
    goToEnd,
    playing,

    // Segment controls
    segmentStart,
    segmentEnd,
    isSegmentMode,
    enableSegmentMode,
    playSegment,
    loopSegment,

    // Audio Channel Controls
    channelControls,
    channelCount,
    isolatedChannelIndex,
    onChannelIsolation,
    onChannelMute,
    onChannelVolumeChange,

    // Keyboard shortcuts
    handleKeyDownShortcuts,

    // time Utility
    getTimeFormatted,

    // reset
    reset
  }
}
